/**
 * In-place document preview modal.
 *
 * Renders a PDF or image inline against the API's
 * /v1/documents/{id}/content route. The Cloudflare Pages proxy
 * forwards /api/* to the Fly origin, so cookies attach naturally
 * and the iframe loads under the operator's session. Non-PDF /
 * non-image types fall back to an "open in new tab" affordance
 * rather than guessing at a renderer.
 *
 * Shared between the Contacts surface (where the operator clicks
 * a head-contract or other-files entry) and the Workspace surface
 * (where the operator clicks a variation submission in the
 * document tree). Keeping the modal in one module guarantees the
 * two surfaces behave identically.
 */

import { useEffect } from "react";
import { loadConfig, type DocumentRecord } from "../api/client";

export function FileViewerModal({
  doc,
  onClose,
}: {
  doc: DocumentRecord;
  onClose: () => void;
}) {
  const cfg = loadConfig();
  const url = `${cfg.baseUrl}/v1/documents/${encodeURIComponent(doc.document_id)}/content`;
  const filename = doc.original_filename.toLowerCase();
  const isPdf = filename.endsWith(".pdf");
  const isImage = /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(filename);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="dialog-backdrop" onClick={onClose} role="presentation">
      <div
        className="dialog dialog-viewer"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label={`Viewing ${doc.original_filename}`}
      >
        <div className="dialog-viewer-head">
          <span className="dialog-viewer-title">{doc.original_filename}</span>
          <div className="dialog-viewer-actions">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm"
            >
              Open in new tab
            </a>
            <button type="button" className="btn btn-sm" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
        <div className="dialog-viewer-body">
          {isPdf ? (
            <iframe
              src={url}
              title={doc.original_filename}
              className="dialog-viewer-iframe"
            />
          ) : isImage ? (
            <img
              src={url}
              alt={doc.original_filename}
              className="dialog-viewer-image"
            />
          ) : (
            <div className="dialog-viewer-fallback">
              <p>
                In-browser preview is not supported for this file type.
                Use <strong>Open in new tab</strong> above to download
                or open it externally.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
